import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { RetailInvoicePDF } from 'src/components/RetailInvoice';
import { useAppContext } from 'src/contexts/AppContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  PointOfSalePaymentState,
  SaleCostType,
  SaleDetails,
} from 'src/WebApiController';

import {
  useRetailSaleInvoiceData,
  useSaleInvoiceCatalogData,
} from './LaunchDownloadSaleInvoice.hooks';

export const LaunchDownloadRetailSaleInvoice = ({
  sale,
  variant,
  iconOnlyMode,
  disabled,
}: {
  sale: SaleDetails;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const { loginContext } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const { event, venue, buyer, eligibleToDownload } =
    useSaleInvoiceCatalogData(sale);
  const { buyerAddress, buyerPaymentMethods } = useRetailSaleInvoiceData(sale);

  const generateAndDownloadRetailPDF = useCallback(async () => {
    setIsLoading(true);
    try {
      const defaultPaymentMethod =
        buyerPaymentMethods?.find((pm) => pm.isDefault) ??
        buyerPaymentMethods?.[0];
      const paymentMethodDisplay = defaultPaymentMethod
        ? `${defaultPaymentMethod.cardBrand ?? ''} ****${defaultPaymentMethod.cardSummary ?? ''}`.trim()
        : undefined;

      const totalAmount = sale.ttlNetProcs?.amt ?? 0;
      const paymentsMade = (sale.mkpLines ?? [])
        .filter(
          (line) =>
            line.type === MarketplacePaymentLineType.Proceeds &&
            line.state === MarketplacePaymentState.Paid
        )
        .reduce((sum, line) => sum + (line.amt?.amt ?? 0), 0);
      const balanceDue = totalAmount - paymentsMade;
      const chargesCredits = (sale.lineItems ?? []).map((li) => ({
        description: li.costType === SaleCostType.Charge ? 'Charge' : 'Credit',
        amount: (li.costType === SaleCostType.Charge ? -1 : 1) * li.cost,
      }));

      const invoiceLineItems = [
        { description: 'Proceeds', amount: totalAmount },
        ...chargesCredits,
      ];

      const blob = await pdf(
        <RetailInvoicePDF
          brokerName={loginContext?.user?.activeAccount?.accountName ?? ''}
          brokerEmail={loginContext?.user?.userName ?? undefined}
          buyerName={sale.transDest?.name ?? buyer?.name}
          buyerEmail={sale.transDest?.email ?? buyer?.email}
          buyerAddress={buyerAddress}
          invoiceNumber={sale.idOnMkp ?? `${sale.id}`}
          invoiceDate={sale.saleDate}
          currency={sale.currency}
          lineItems={invoiceLineItems}
          status={
            sale.pmtState === PointOfSalePaymentState.Paid
              ? 'Paid'
              : 'Outstanding'
          }
          totalAmount={totalAmount}
          balanceDue={balanceDue}
          paymentsMade={paymentsMade}
          brokerNotes={sale.privNotes ?? undefined}
          paymentMethod={paymentMethodDisplay}
          eventName={event?.name}
          eventDate={event?.dates?.start ?? undefined}
          venueName={venue?.name}
          seatingSection={sale.seating?.section}
          seatingRow={sale.seating?.row ?? undefined}
          seatFrom={sale.seating?.seatFr ?? undefined}
          seatTo={sale.seating?.seatTo ?? undefined}
          ticketQty={sale.qtySold}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailInvoice_${sale.id}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating retail invoice PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [buyer, buyerPaymentMethods, sale]);

  const iconFill =
    iconOnlyMode || (variant && variant !== 'regular')
      ? vars.color.textBrand
      : vars.color.textInverted;

  const buttonDisabled = disabled || isLoading || !eligibleToDownload;

  return (
    <ButtonWithIcon
      disabled={buttonDisabled}
      variant={variant}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
      textContentId={ContentId.PrintInvoice}
      onClick={buttonDisabled ? undefined : generateAndDownloadRetailPDF}
      icon={
        isLoading ? (
          <RotatingWrapper>
            <ProcessingIcon fill={iconFill} />
          </RotatingWrapper>
        ) : (
          <DownloadIcon
            withHoverEffect
            disabled={buttonDisabled}
            fill={iconFill}
            size={vars.iconSize.m}
          />
        )
      }
    />
  );
};
